(function () {
  let appDataPromise = null;
  let activeOverlay = null;
  let barcodeLibraryPromise = null;
  let activeHeaderElement = null;
  let destroyHeaderController = null;
  let headerTeardownPromise = Promise.resolve();
  let restoreHeaderFocusOnMount = false;

  function createBarcodeScannerLifecycle({ onStateChange, clearReader, stopMediaTracks, reportError } = {}) {
    let activeSession = null;
    let nextSessionId = 0;

    const notify = () => {
      if (typeof onStateChange === "function") {
        onStateChange(activeSession);
      }
    };

    const report = (error, phase) => {
      if (typeof reportError === "function") {
        reportError(error, phase);
      }
    };

    async function forceStopTracks(session, scanner, phase) {
      if (typeof stopMediaTracks !== "function") return;

      try {
        await stopMediaTracks(session, scanner);
      } catch (error) {
        report(error, phase);
      }
    }

    async function releaseScanner(session, scanner, shouldStop) {
      if (!scanner) return;

      // Privacy first: stop any tracks already attached before awaiting vendor cleanup.
      await forceStopTracks(session, scanner, "pre-stop-tracks");

      if (shouldStop && typeof scanner.stop === "function") {
        try {
          await scanner.stop();
        } catch (error) {
          report(error, "stop");
        }
      }

      // The vendor may attach or replace a stream while stop() is settling.
      await forceStopTracks(session, scanner, "post-stop-tracks");

      if (typeof scanner.clear === "function") {
        try {
          await scanner.clear();
        } catch (error) {
          report(error, "clear");
        }
      }

      if (session.scanner === scanner) {
        session.scanner = null;
        session.started = false;
      }
    }

    function cleanupSession(session) {
      if (!session) return Promise.resolve();
      if (session.cleanupPromise) return session.cleanupPromise;

      session.cancelled = true;
      session.phase = "closing";
      notify();

      session.cleanupPromise = (async () => {
        const pendingStart = session.startPromise;
        if (pendingStart && session.scanner) {
          await forceStopTracks(session, session.scanner, "pending-tracks");
        }

        if (pendingStart) {
          try {
            await pendingStart;
          } catch (error) {
            // A rejected start still needs its reader and any provisional tracks cleared.
          }
        }

        await releaseScanner(session, session.scanner, session.started);

        if (activeSession === session) {
          activeSession = null;
        }
        session.phase = "closed";

        if (typeof clearReader === "function") {
          clearReader(session);
        }
        notify();
      })();

      return session.cleanupPromise;
    }

    function beginSession() {
      if (activeSession) return null;

      activeSession = {
        id: ++nextSessionId,
        phase: "preparing",
        cancelled: false,
        scanner: null,
        started: false,
        startPromise: null,
        cleanupPromise: null,
      };
      notify();
      return activeSession;
    }

    async function startScanner(session, scanner, start) {
      if (!session || activeSession !== session || session.cancelled) {
        return { started: false, cancelled: true };
      }

      session.scanner = scanner;
      session.started = false;
      session.phase = "starting";
      notify();

      let pendingStart;
      try {
        pendingStart = Promise.resolve(start());
        session.startPromise = pendingStart;
        await pendingStart;
        session.started = true;
      } catch (error) {
        if (session.startPromise === pendingStart) {
          session.startPromise = null;
        }

        if (session.cancelled) {
          await cleanupSession(session);
          return { started: false, cancelled: true };
        }

        session.phase = "preparing";
        notify();
        throw error;
      }

      if (session.startPromise === pendingStart) {
        session.startPromise = null;
      }

      if (session.cancelled) {
        await cleanupSession(session);
        return { started: false, cancelled: true };
      }

      session.phase = "running";
      notify();
      return { started: true, cancelled: false };
    }

    async function discardScanner(session, scanner) {
      if (!session || activeSession !== session) return;
      if (session.cancelled) {
        await cleanupSession(session);
        return;
      }

      await releaseScanner(session, scanner, false);
      session.phase = "preparing";
      notify();
    }

    return {
      beginSession,
      startScanner,
      discardScanner,
      cancelActive: () => cleanupSession(activeSession),
      failSession: (session) => cleanupSession(session),
      waitForIdle: () => (activeSession ? cleanupSession(activeSession) : Promise.resolve()),
      getActiveSession: () => activeSession,
      isCurrent: (session) => activeSession === session && !session.cancelled,
    };
  }

  function createIcons() {
    if (window.lucide) {
      window.lucide.createIcons();
    }
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function pluralize(value, singular, plural) {
    return value === 1 ? singular : plural;
  }

  function sanitizeCustomerName(value, fallback = "Collector") {
    const cleaned = String(value == null ? "" : value).trim();
    if (!cleaned || cleaned.includes("{{") || cleaned.includes("{%")) {
      return fallback;
    }

    return cleaned;
  }

  function formatDateTime(value, timeZone) {
    if (!value) return "Date TBA";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date TBA";

    const options = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    };

    if (timeZone) {
      options.timeZone = timeZone;
    }

    try {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    } catch (error) {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
    }
  }

  function fetchAppData() {
    if (!appDataPromise) {
      appDataPromise = fetch("/apps/portal/data", {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Darkstone public feed failed to load.");
          }

          return response.json();
        })
        .catch((error) => {
          appDataPromise = null;
          throw error;
        });
    }

    return appDataPromise;
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = hidden;
  }

  function loadBarcodeLibrary(scriptUrl) {
    if (window.Html5Qrcode && window.Html5QrcodeSupportedFormats) {
      return Promise.resolve();
    }

    if (!scriptUrl) {
      return Promise.reject(new Error("Barcode scanner asset URL is missing."));
    }

    if (barcodeLibraryPromise) {
      return barcodeLibraryPromise;
    }

    barcodeLibraryPromise = new Promise((resolve, reject) => {
      const resolveLoadedScript = (script) => {
        if (window.Html5Qrcode && window.Html5QrcodeSupportedFormats) {
          script.dataset.pulseBarcodeState = "loaded";
          resolve();
          return;
        }

        script.dataset.pulseBarcodeState = "failed";
        script.remove();
        reject(new Error("Barcode scanner loaded without the expected API."));
      };

      const rejectFailedScript = (script) => {
        script.dataset.pulseBarcodeState = "failed";
        script.remove();
        reject(new Error("Barcode scanner failed to load."));
      };

      const existing = document.querySelector("script[data-pulse-barcode-library]");
      if (existing?.dataset.pulseBarcodeState === "loading") {
        existing.addEventListener("load", () => resolveLoadedScript(existing), { once: true });
        existing.addEventListener("error", () => rejectFailedScript(existing), { once: true });
        return;
      }

      if (existing) {
        existing.remove();
      }

      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.dataset.pulseBarcodeLibrary = "true";
      script.dataset.pulseBarcodeState = "loading";
      script.onload = () => resolveLoadedScript(script);
      script.onerror = () => rejectFailedScript(script);
      document.head.appendChild(script);
    }).catch((error) => {
      barcodeLibraryPromise = null;
      throw error;
    });

    return barcodeLibraryPromise;
  }

  function normalizeBarcodeValue(value) {
    const raw = String(value == null ? "" : value).trim();
    if (!raw) return "";

    const digitsOnly = raw.replace(/\D/g, "");
    if (digitsOnly.length >= 4) {
      return digitsOnly;
    }

    return raw.replace(/\s+/g, "");
  }

  function buildBarcodeSearchUrl(value) {
    const normalized = normalizeBarcodeValue(value);
    if (!normalized) return "";

    const query = normalized.length >= 17 ? normalized : `${normalized}*`;
    return `/search?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last`;
  }

  async function getNativeBarcodeSupport(formatDefinitions) {
    if (!window.BarcodeDetector || typeof window.BarcodeDetector.getSupportedFormats !== "function") {
      return {
        supportedFormatNames: [],
        supportsNativeDetection: false,
      };
    }

    try {
      const nativeSupported = await window.BarcodeDetector.getSupportedFormats();
      const supportedSet = new Set((nativeSupported || []).map((value) => String(value || "").toLowerCase()));
      const supportedFormatNames = formatDefinitions
        .filter((definition) => supportedSet.has(definition.nativeName))
        .map((definition) => definition.html5Format)
        .filter((format) => format !== undefined && format !== null);

      return {
        supportedFormatNames,
        supportsNativeDetection: supportedFormatNames.length > 0,
      };
    } catch (error) {
      console.warn("[Pulse v2] BarcodeDetector format probe failed.", error);
      return {
        supportedFormatNames: [],
        supportsNativeDetection: false,
      };
    }
  }

  async function getAvailableBarcodeCameras(Html5Qrcode) {
    if (typeof Html5Qrcode?.getCameras !== "function") {
      return [];
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      return Array.isArray(cameras) ? cameras : [];
    } catch (error) {
      console.warn("[Pulse v2] Barcode camera enumeration failed.", error);
      return [];
    }
  }

  function syncBodyState() {
    const overlays = Array.from(
      document.querySelectorAll("#pulse-mobile-drawer, #pulse-cart-panel, [data-pulse-barcode-modal]"),
    );
    const hasVisibleOverlay = overlays.some((overlay) => !overlay.hidden);
    document.documentElement.style.overflow = hasVisibleOverlay ? "hidden" : "";
    document.body.classList.toggle("has-pulse-bottom-nav", Boolean(document.querySelector(".pulse-bottom-nav")));
  }

  function openOverlay(panel, toggleButton) {
    if (!panel) return;

    if (activeOverlay && activeOverlay !== panel) {
      closeOverlay(activeOverlay);
    }

    setHidden(panel, false);
    activeOverlay = panel;

    if (toggleButton) {
      toggleButton.setAttribute("aria-expanded", "true");
    }

    syncBodyState();
  }

  function closeOverlay(panel, toggleButton) {
    if (!panel) return;

    setHidden(panel, true);

    if (toggleButton) {
      toggleButton.setAttribute("aria-expanded", "false");
    }

    if (activeOverlay === panel) {
      activeOverlay = null;
    }

    syncBodyState();
  }

  async function refreshHeaderCart() {
    try {
      const response = await fetch("/?sections=header", {
        headers: {
          Accept: "application/json",
        },
      });
      const payload = await response.json();
      const html = payload.header || payload[Object.keys(payload)[0]];

      if (!html) return;

      const temp = document.createElement("div");
      temp.innerHTML = html;

      const nextCount = temp.querySelector("[data-pulse-cart-count]");
      const nextPanel = temp.querySelector("[data-pulse-cart-panel]");
      const currentCount = document.querySelector("[data-pulse-cart-count]");
      const currentPanel = document.querySelector("[data-pulse-cart-panel]");

      if (currentCount && nextCount) {
        currentCount.textContent = nextCount.textContent;
      }

      if (currentPanel && nextPanel) {
        currentPanel.innerHTML = nextPanel.innerHTML;
      }

      createIcons();
      syncBodyState();
    } catch (error) {
      console.error("[Pulse v2] Failed to refresh header cart.", error);
    }
  }

  function initHeader(headerRoot, previousHeaderCleanup = Promise.resolve()) {
    const header = headerRoot || document.querySelector("[data-pulse-header]");
    if (!header) return null;

    const headerEvents = new AbortController();

    const drawer = document.getElementById("pulse-mobile-drawer");
    const cartPanel = document.getElementById("pulse-cart-panel");
    const menuToggle = header.querySelector("[data-pulse-menu-toggle]");
    const cartToggle = header.querySelector("[data-pulse-cart-toggle]");
    const accountToggle = header.querySelector("[data-pulse-account-toggle]");
    const accountDropdown = header.querySelector("[data-pulse-account-dropdown]");
    const barcodeModal = header.querySelector("[data-pulse-barcode-modal]");
    const barcodeDialog = barcodeModal ? barcodeModal.querySelector("[role=\"dialog\"]") : null;
    const barcodeCloseButton = barcodeModal ? barcodeModal.querySelector("button[data-pulse-barcode-close]") : null;
    const barcodeStatus = barcodeModal ? barcodeModal.querySelector("[data-pulse-barcode-status]") : null;
    const barcodeReader = barcodeModal ? barcodeModal.querySelector("[data-pulse-barcode-reader]") : null;
    const barcodeScriptUrl = barcodeModal ? barcodeModal.dataset.pulseBarcodeScriptUrl || "" : "";
    const barcodeBackgroundState = new Map();
    let barcodePendingPrimary = "";
    let barcodePendingTimer = null;
    let barcodeReturnFocus = null;
    let barcodeOpenRequestToken = 0;
    let lastFocusedSearchInput = null;
    let lastScannedValue = "";
    let lastScannedAt = 0;

    if (barcodeModal && barcodeModal.parentElement !== document.body) {
      document.body.appendChild(barcodeModal);
    }

    function closeAccountDropdown() {
      if (!accountDropdown) return;
      accountDropdown.hidden = true;
      if (accountToggle) {
        accountToggle.setAttribute("aria-expanded", "false");
      }
    }

    function openAccountDropdown() {
      if (!accountDropdown) return;
      closeOverlay(drawer, menuToggle);
      closeOverlay(cartPanel, cartToggle);
      accountDropdown.hidden = false;
      if (accountToggle) {
        accountToggle.setAttribute("aria-expanded", "true");
      }
    }

    function setBarcodeStatus(message, tone = "default") {
      if (!barcodeStatus) return;
      barcodeStatus.setAttribute("aria-live", tone === "error" ? "assertive" : "polite");
      barcodeStatus.dataset.tone = tone;
      barcodeStatus.textContent = message;
    }

    function clearPendingBarcode() {
      if (barcodePendingTimer) {
        window.clearTimeout(barcodePendingTimer);
        barcodePendingTimer = null;
      }
      barcodePendingPrimary = "";
    }

    function getBarcodeStartErrorMessage(error) {
      const rawErrorName = String(error?.name || "").trim();
      const rawErrorMessage = String(error?.message || error || "").trim();
      const errorName = rawErrorName.toLowerCase();
      const errorMessage = rawErrorMessage.toLowerCase();

      if (errorName.includes("notallowed") || /permission|denied|blocked/.test(errorMessage)) {
        return "Camera permission is blocked. Allow camera access for Darkstone Comics in your browser settings, then try again.";
      }

      if (errorName.includes("notfound") || /no camera|requested device not found/.test(errorMessage)) {
        return "No usable camera was found on this device. You can still use the regular search box.";
      }

      if (errorName.includes("notreadable") || /could not start video|track start|in use/.test(errorMessage)) {
        return "The camera is busy in another app. Close the other camera app, then try again.";
      }

      if (/secure browser context|camera support/.test(errorMessage)) {
        return "This browser cannot start a secure camera session. Open Darkstone Comics directly in Safari or Chrome and try again.";
      }

      const diagnosticDetail = [rawErrorName, rawErrorMessage]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(": ")
        .replace(/\s+/g, " ")
        .slice(0, 140);
      const diagnosticSuffix = diagnosticDetail ? ` Technical detail: ${diagnosticDetail}` : "";
      return `We couldn't start the camera scanner. Check camera permission in your browser settings and try again. You can still use regular search.${diagnosticSuffix}`;
    }

    function setBarcodeBackgroundInert(shouldBeInert) {
      if (!barcodeModal) return;

      if (shouldBeInert) {
        Array.from(document.body.children).forEach((element) => {
          if (!(element instanceof HTMLElement) || element === barcodeModal || barcodeBackgroundState.has(element)) return;
          barcodeBackgroundState.set(element, {
            inert: Boolean(element.inert),
            ariaHidden: element.getAttribute("aria-hidden"),
          });
          element.inert = true;
          element.setAttribute("aria-hidden", "true");
        });
        return;
      }

      barcodeBackgroundState.forEach((state, element) => {
        element.inert = state.inert;
        if (state.ariaHidden == null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", state.ariaHidden);
        }
      });
      barcodeBackgroundState.clear();
    }

    function focusBarcodeDialog() {
      if (!barcodeModal?.hidden) {
        barcodeCloseButton?.focus({ preventScroll: true });
      }
      window.requestAnimationFrame(() => {
        if (!barcodeModal?.hidden) {
          barcodeCloseButton?.focus({ preventScroll: true });
        }
      });
    }

    const barcodeLifecycle = createBarcodeScannerLifecycle({
      clearReader: () => {
        if (barcodeReader) {
          barcodeReader.innerHTML = "";
        }
      },
      stopMediaTracks: () => {
        if (!barcodeReader) return;
        barcodeReader.querySelectorAll("video").forEach((video) => {
          const stream = video.srcObject;
          if (stream && typeof stream.getTracks === "function") {
            stream.getTracks().forEach((track) => track.stop());
          }
          video.srcObject = null;
        });
      },
      reportError: (error, phase) => {
        console.warn(`[Pulse v2] Barcode scanner ${phase} cleanup warning.`, error);
      },
    });

    function closeAppHubDrawer() {
      document.dispatchEvent(new CustomEvent("darkstone:close-app-hub"));

      const hubDrawer = document.querySelector("[data-app-hub-drawer]");
      const hubBackdrop = document.querySelector("[data-app-hub-backdrop]");
      hubDrawer?.setAttribute("aria-hidden", "true");
      hubDrawer?.classList.remove("is-open");
      hubBackdrop?.classList.remove("is-open");
      document.body.classList.remove("dshub-body-locked");
      document.querySelectorAll("[data-app-hub-trigger]").forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });
    }

    function openBarcodeModal(trigger, statusMessage = "Starting camera...") {
      if (!barcodeModal) return;
      closeAppHubDrawer();
      closeOverlay(drawer, menuToggle);
      closeOverlay(cartPanel, cartToggle);
      closeAccountDropdown();
      barcodeReturnFocus = trigger || barcodeReturnFocus;
      lastFocusedSearchInput = trigger?.closest("form")?.querySelector(".pulse-header__search-input") || null;
      setBarcodeBackgroundInert(true);
      setHidden(barcodeModal, false);
      setBarcodeStatus(statusMessage, "default");
      syncBodyState();
      focusBarcodeDialog();
    }

    function closeBarcodeModal({ restoreFocus = true } = {}) {
      if (!barcodeModal) return Promise.resolve();

      barcodeOpenRequestToken += 1;
      clearPendingBarcode();
      setHidden(barcodeModal, true);
      setBarcodeBackgroundInert(false);
      syncBodyState();

      const returnFocus = barcodeReturnFocus;
      barcodeReturnFocus = null;
      if (restoreFocus && returnFocus?.isConnected) {
        returnFocus.focus({ preventScroll: true });
      }

      return barcodeLifecycle.cancelActive();
    }

    function submitBarcodeSearch(value) {
      const normalized = normalizeBarcodeValue(value);
      if (!normalized) return;

      const url = buildBarcodeSearchUrl(normalized);
      if (!url) return;

      if (lastFocusedSearchInput) {
        lastFocusedSearchInput.value = normalized;
      }

      setBarcodeStatus(`Opening results for ${normalized}...`, "success");
      closeBarcodeModal({ restoreFocus: false }).finally(() => {
        window.location.assign(url);
      });
    }

    function handleBarcodeResult(decodedText, decodedResult) {
      const normalized = normalizeBarcodeValue(decodedText);
      if (!normalized) return;

      const now = Date.now();
      if (normalized === lastScannedValue && now - lastScannedAt < 1200) {
        return;
      }

      lastScannedValue = normalized;
      lastScannedAt = now;

      const formatName = String(decodedResult?.result?.format?.formatName || "").toUpperCase();

      if (normalized.length >= 17) {
        submitBarcodeSearch(normalized);
        return;
      }

      if ((formatName.includes("UPC_EAN_EXTENSION") || normalized.length === 5) && barcodePendingPrimary) {
        submitBarcodeSearch(`${barcodePendingPrimary}${normalized}`);
        return;
      }

      if (normalized.length >= 12) {
        clearPendingBarcode();
        barcodePendingPrimary = normalized;
        setBarcodeStatus(`Barcode ${normalized} found. Checking for an issue add-on...`, "success");
        barcodePendingTimer = window.setTimeout(() => submitBarcodeSearch(normalized), 650);
        return;
      }

      if (normalized.length >= 8) {
        submitBarcodeSearch(normalized);
      }
    }

    async function startBarcodeScanner(trigger) {
      if (!barcodeModal || !barcodeReader) return;

      const currentSession = barcodeLifecycle.getActiveSession();
      if (currentSession && !currentSession.cancelled) return;

      const requestToken = ++barcodeOpenRequestToken;
      openBarcodeModal(trigger, currentSession ? "Resetting camera..." : "Starting camera...");

      await previousHeaderCleanup;
      if (requestToken !== barcodeOpenRequestToken || barcodeModal.hidden) return;

      if (currentSession) {
        await barcodeLifecycle.waitForIdle();
        if (requestToken !== barcodeOpenRequestToken || barcodeModal.hidden) return;
      }

      const session = barcodeLifecycle.beginSession();
      if (!session) return;

      try {
        await loadBarcodeLibrary(barcodeScriptUrl);
        if (!barcodeLifecycle.isCurrent(session) || requestToken !== barcodeOpenRequestToken || barcodeModal.hidden) return;

        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
          throw new Error("Barcode scanning requires a secure browser context with camera support.");
        }

        const formatDefinitions = [
          { html5Name: "EAN_13", nativeName: "ean_13" },
          { html5Name: "EAN_8", nativeName: "ean_8" },
          { html5Name: "UPC_A", nativeName: "upc_a" },
          { html5Name: "UPC_E", nativeName: "upc_e" },
          { html5Name: "UPC_EAN_EXTENSION", nativeName: "upc_ean_extension" },
          { html5Name: "CODE_128", nativeName: "code_128" },
          { html5Name: "CODE_39", nativeName: "code_39" },
          { html5Name: "CODABAR", nativeName: "codabar" },
        ].map((definition) => ({
          ...definition,
          html5Format: window.Html5QrcodeSupportedFormats?.[definition.html5Name],
        }));

        const fallbackFormats = formatDefinitions
          .map((definition) => definition.html5Format)
          .filter((format) => format !== undefined && format !== null);

        const startPlans = [
          {
            label: "html5-fallback",
            useBarCodeDetectorIfSupported: false,
            formatsToSupport: fallbackFormats,
          },
        ];

        const config = {
          fps: 10,
          qrbox: { width: 280, height: 160 },
          aspectRatio: 1.7777778,
          disableFlip: false,
        };
        const onSuccess = (decodedText, decodedResult) => handleBarcodeResult(decodedText, decodedResult);
        const onError = () => {};

        let fallbackCamera = null;
        let fallbackCameraResolved = false;
        let startError = null;
        let started = false;

        for (const plan of startPlans) {
          if (!barcodeLifecycle.isCurrent(session)) return;

          let scanner = null;
          try {
            scanner = new window.Html5Qrcode(barcodeReader.id, {
              formatsToSupport: plan.formatsToSupport,
              useBarCodeDetectorIfSupported: plan.useBarCodeDetectorIfSupported,
              verbose: false,
            });

            const result = await barcodeLifecycle.startScanner(
              session,
              scanner,
              () => scanner.start({ facingMode: { ideal: "environment" } }, config, onSuccess, onError),
            );
            if (result.cancelled) return;
            started = result.started;
            startError = null;
          } catch (primaryError) {
            if (!barcodeLifecycle.isCurrent(session)) return;
            startError = primaryError;

            if (scanner) {
              await barcodeLifecycle.discardScanner(session, scanner);
              if (!barcodeLifecycle.isCurrent(session)) return;
              scanner = null;
            }

            if (!fallbackCameraResolved) {
              const cameras = await getAvailableBarcodeCameras(window.Html5Qrcode);
              if (!barcodeLifecycle.isCurrent(session)) return;
              fallbackCamera =
                cameras.find((camera) => /back|rear|environment/i.test(String(camera.label || ""))) || cameras[0] || null;
              fallbackCameraResolved = true;
            }

            if (fallbackCamera) {
              try {
                scanner = new window.Html5Qrcode(barcodeReader.id, {
                  formatsToSupport: plan.formatsToSupport,
                  useBarCodeDetectorIfSupported: plan.useBarCodeDetectorIfSupported,
                  verbose: false,
                });

                const result = await barcodeLifecycle.startScanner(
                  session,
                  scanner,
                  () => scanner.start(fallbackCamera.id, config, onSuccess, onError),
                );
                if (result.cancelled) return;
                started = result.started;
                startError = null;
              } catch (fallbackError) {
                if (!barcodeLifecycle.isCurrent(session)) return;
                startError = fallbackError;
              }
            }
          }

          if (started) break;

          console.warn(`[Pulse v2] Barcode scanner start plan failed: ${plan.label}.`, startError);
          if (scanner) {
            await barcodeLifecycle.discardScanner(session, scanner);
          }
        }

        if (!started) {
          throw startError || new Error("No camera start plan succeeded.");
        }

        if (!barcodeLifecycle.isCurrent(session) || barcodeModal.hidden) return;
        setBarcodeStatus(
          "Point your camera at a barcode. We'll search by the full comic code when we get it, with barcode-prefix fallback for the rest.",
          "default",
        );
        focusBarcodeDialog();
      } catch (error) {
        if (session.cancelled) return;
        if (barcodeLifecycle.isCurrent(session)) {
          console.error("[Pulse v2] Barcode scanner failed to start.", error);
          setBarcodeStatus(getBarcodeStartErrorMessage(error), "error");
          focusBarcodeDialog();
        }
        await barcodeLifecycle.failSession(session);
      }
    }

    document.addEventListener("click", (event) => {
      const menuOpen = event.target.closest("[data-pulse-menu-toggle]");
      const menuClose = event.target.closest("[data-pulse-menu-close]");
      const cartOpen = event.target.closest("[data-pulse-cart-toggle]");
      const cartClose = event.target.closest("[data-pulse-cart-close]");
      const accountOpen = event.target.closest("[data-pulse-account-toggle]");
      const accountInside = event.target.closest("[data-pulse-account-dropdown]");
      const barcodeOpen = event.target.closest("[data-pulse-barcode-trigger]");
      const barcodeClose = event.target.closest("[data-pulse-barcode-close]");
      const drawerLink = event.target.closest(".pulse-header__drawer-link, .pulse-header__drawer-sublink, .pulse-header__drawer-actions a");

      if (menuOpen) {
        closeAccountDropdown();
        if (drawer && !drawer.hidden) {
          closeOverlay(drawer, menuToggle);
        } else {
          closeOverlay(cartPanel, cartToggle);
          openOverlay(drawer, menuToggle);
        }
      }

      if (menuClose) {
        closeOverlay(drawer, menuToggle);
      }

      if (cartOpen) {
        closeAccountDropdown();
        if (cartPanel && !cartPanel.hidden) {
          closeOverlay(cartPanel, cartToggle);
        } else {
          closeOverlay(drawer, menuToggle);
          openOverlay(cartPanel, cartToggle);
        }
      }

      if (cartClose) {
        closeOverlay(cartPanel, cartToggle);
      }

      if (barcodeOpen) {
        event.preventDefault();
        startBarcodeScanner(barcodeOpen);
      }

      if (barcodeClose) {
        event.preventDefault();
        closeBarcodeModal();
      }

      if (accountOpen) {
        if (accountDropdown && !accountDropdown.hidden) {
          closeAccountDropdown();
        } else {
          openAccountDropdown();
        }
      }

      if (drawerLink) {
        closeOverlay(drawer, menuToggle);
      }

      if (accountDropdown && !accountOpen && !accountInside) {
        closeAccountDropdown();
      }
    }, { signal: headerEvents.signal });

    document.addEventListener("keydown", (event) => {
      const barcodeIsOpen = Boolean(barcodeModal && !barcodeModal.hidden);

      if (event.key === "Escape") {
        closeOverlay(drawer, menuToggle);
        closeOverlay(cartPanel, cartToggle);
        closeAccountDropdown();
        if (barcodeIsOpen) {
          event.preventDefault();
          closeBarcodeModal();
        }
        return;
      }

      if (event.key !== "Tab" || !barcodeIsOpen || !barcodeDialog) return;

      const focusable = Array.from(
        barcodeDialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        barcodeDialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !barcodeDialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !barcodeDialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }, { signal: headerEvents.signal });

    window.addEventListener("pagehide", () => {
      closeBarcodeModal({ restoreFocus: false });
    }, { signal: headerEvents.signal });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && barcodeModal && !barcodeModal.hidden) {
        closeBarcodeModal({ restoreFocus: false });
      }
    }, { signal: headerEvents.signal });

    window.addEventListener("resize", () => {
      if (window.innerWidth > 820) {
        closeOverlay(drawer, menuToggle);
      }
      closeAccountDropdown();
      syncBodyState();
    }, { signal: headerEvents.signal });

    if (barcodeScriptUrl && window.matchMedia?.("(max-width: 820px)").matches) {
      loadBarcodeLibrary(barcodeScriptUrl).catch((error) => {
        console.warn("[Pulse v2] Barcode scanner warm-up failed; the next tap will retry.", error);
      });
    }

    syncBodyState();

    return () => {
      headerEvents.abort();
      closeOverlay(drawer, menuToggle);
      closeOverlay(cartPanel, cartToggle);
      closeAccountDropdown();
      const cleanup = closeBarcodeModal({ restoreFocus: false });
      barcodeModal?.remove();
      syncBodyState();
      return cleanup;
    };
  }

  function initHero(root) {
    const slides = Array.from(root.querySelectorAll("[data-pulse-hero-slide]"));
    const dots = Array.from(root.querySelectorAll("[data-pulse-hero-dot]"));
    const prev = root.querySelector("[data-pulse-hero-prev]");
    const next = root.querySelector("[data-pulse-hero-next]");
    const viewport = root.querySelector(".pulse-hero__viewport");
    const autoplay = root.getAttribute("data-autoplay") === "true";
    const interval = Number(root.getAttribute("data-interval") || 6000);
    let index = slides.findIndex((slide) => slide.classList.contains("is-active"));
    let timer = null;
    let touchStartX = null;
    let touchEndX = null;

    if (!slides.length) return;
    if (index < 0) index = 0;

    function render(nextIndex) {
      index = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function restart() {
      if (timer) {
        window.clearInterval(timer);
      }

      if (autoplay && slides.length > 1) {
        timer = window.setInterval(() => render(index + 1), Math.max(interval, 2500));
      }
    }

    function handleSwipe() {
      if (touchStartX == null || touchEndX == null) return;

      const delta = touchEndX - touchStartX;
      if (Math.abs(delta) >= 40) {
        render(delta > 0 ? index - 1 : index + 1);
        restart();
      }

      touchStartX = null;
      touchEndX = null;
    }

    if (prev) {
      prev.addEventListener("click", () => {
        render(index - 1);
        restart();
      });
    }

    if (next) {
      next.addEventListener("click", () => {
        render(index + 1);
        restart();
      });
    }

    dots.forEach((dot, dotIndex) => {
      dot.addEventListener("click", () => {
        render(dotIndex);
        restart();
      });
    });

    if (viewport && slides.length > 1) {
      viewport.addEventListener(
        "touchstart",
        (event) => {
          touchStartX = event.touches[0] ? event.touches[0].clientX : null;
          touchEndX = null;
        },
        { passive: true },
      );

      viewport.addEventListener(
        "touchmove",
        (event) => {
          touchEndX = event.touches[0] ? event.touches[0].clientX : null;
        },
        { passive: true },
      );

      viewport.addEventListener("touchend", handleSwipe);
    }

    render(index);
    restart();
  }

  function updatePullPanel(root, data) {
    const active = root.querySelector("[data-pulse-pull-active]");
    const history = root.querySelector("[data-pulse-pull-history]");
    const open = root.querySelector("[data-pulse-pull-open]");
    const summary = root.querySelector("[data-pulse-pull-summary-text]");

    if (!data || !data.authenticated) {
      if (active) active.textContent = "0";
      if (history) history.textContent = "0";
      if (open) open.textContent = "0";
      if (summary) {
        summary.textContent = "Sign in to see your active holds, recent history, and open reservation volume.";
      }
      return;
    }

    const activeCount = Number(data.pullList?.activeCount || 0);
    const historyCount = Number(data.pullList?.historyCount || 0);
    const reservableCount = Number(data.pullList?.reservableCount || 0);

    if (active) active.textContent = String(activeCount);
    if (history) history.textContent = String(historyCount);
    if (open) open.textContent = String(reservableCount);

    if (summary) {
      const firstName = sanitizeCustomerName(data.customer?.firstName, "Collector");
      summary.textContent =
        `${firstName}, you have ${activeCount} active ${pluralize(activeCount, "hold", "holds")} ` +
        `and ${reservableCount} upcoming ${pluralize(reservableCount, "book", "books")} ready to review.`;
    }
  }

  function seatsLabel(event) {
    const capacity = Number(event?.capacity);
    const remainingSeats = Number(event?.remainingSeats);
    const soldCount = Number(event?.soldCount);

    if (Number.isFinite(remainingSeats) && Number.isFinite(capacity)) {
      if (remainingSeats <= 0) return "Sold out";
      if (remainingSeats <= 3) return `Only ${remainingSeats} seats left`;
      return `${remainingSeats} of ${capacity} seats left`;
    }

    if (Number.isFinite(remainingSeats)) {
      return remainingSeats <= 0 ? "Sold out" : `${remainingSeats} seats left`;
    }

    if (Number.isFinite(capacity)) {
      return `${capacity} seats available`;
    }

    if (Number.isFinite(soldCount) && soldCount > 0) {
      return `${soldCount} registered | open registration`;
    }

    return "Registration open";
  }

  function renderEventCard(event) {
    const ctaUrl = event.productUrl || "/pages/events";
    const seatText = seatsLabel(event);
    const ticketPrice = Number(event.ticketPrice);
    const priceText =
      Number.isFinite(ticketPrice) && ticketPrice > 0
        ? ticketPrice.toLocaleString(undefined, { style: "currency", currency: "USD" })
        : "Free event";
    const metaPrimary = [formatDateTime(event.startsAt, event.timezone), event.location].filter(Boolean).join(" | ");
    const metaSecondary = [seatText, priceText].filter(Boolean).join(" | ");

    return `
      <article class="pulse-event-card">
        <div class="pulse-event-card__image">
          ${
            event.imageUrl
              ? `<img src="${escapeHtml(event.imageUrl)}" alt="${escapeHtml(event.imageAlt || event.title)}">`
              : `<div class="pulse-product-card__media-fallback">${escapeHtml(event.title)}</div>`
          }
        </div>
        <div class="pulse-event-card__body">
          <p class="pulse-section__eyebrow">Upcoming event</p>
          <h3>${escapeHtml(event.title)}</h3>
          <p class="pulse-event-card__meta pulse-event-card__meta--primary">${escapeHtml(metaPrimary || "Date TBA")}</p>
          <p class="pulse-event-card__meta pulse-event-card__meta--secondary">${escapeHtml(metaSecondary || priceText)}</p>
          ${event.description ? `<p class="pulse-event-card__copy">${escapeHtml(event.description)}</p>` : ""}
          <a href="${escapeHtml(ctaUrl)}" class="pulse-button pulse-button--primary pulse-event-card__cta">Register now</a>
        </div>
      </article>
    `;
  }

  function updateEventsModule(root, data) {
    const live = root.querySelector("[data-pulse-events-live]");
    const fallback = root.querySelector("[data-pulse-events-fallback]");
    const limit = Number(root.getAttribute("data-item-limit") || 3);
    const events = Array.isArray(data?.upcomingEvents) ? data.upcomingEvents.slice(0, limit) : [];

    if (!live) return;

    if (!events.length) {
      live.innerHTML = "";
      if (fallback) fallback.classList.add("is-visible");
      return;
    }

    live.innerHTML = `<div class="pulse-events-module__grid">${events.map((event) => renderEventCard(event)).join("")}</div>`;
    if (fallback) fallback.classList.remove("is-visible");
    createIcons();
  }

  function initFeedModules() {
    const pullPanels = Array.from(document.querySelectorAll("[data-pulse-pull-panel]")).filter(
      (node) => node.getAttribute("data-show-summary") === "true",
    );
    const eventModules = Array.from(document.querySelectorAll("[data-pulse-events-module]")).filter(
      (node) => node.getAttribute("data-source-mode") === "app_feed",
    );

    if (!pullPanels.length && !eventModules.length) return;

    fetchAppData()
      .then((data) => {
        pullPanels.forEach((panel) => updatePullPanel(panel, data));
        eventModules.forEach((module) => updateEventsModule(module, data));
      })
      .catch((error) => {
        console.error("[Pulse v2] Failed to hydrate public feed modules.", error);
        pullPanels.forEach((panel) => updatePullPanel(panel, null));
        eventModules.forEach((module) => {
          const fallback = module.querySelector("[data-pulse-events-fallback]");
          const live = module.querySelector("[data-pulse-events-live]");

          if (fallback) fallback.classList.add("is-visible");
          if (live) live.innerHTML = "";
        });
      });
  }

  function initQuickAdd() {
    document.addEventListener("submit", async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches("[data-pulse-quick-add-form]")) return;

      event.preventDefault();

      const submit = form.querySelector('button[type="submit"]');
      const originalText = submit ? submit.dataset.pulseOriginalText || submit.textContent || "Quick Add" : "Quick Add";

      if (submit) {
        submit.dataset.pulseOriginalText = originalText;
        submit.disabled = true;
        submit.textContent = "Adding...";
      }

      try {
        const response = await fetch(window.routes.cart_add_url || "/cart/add.js", {
          method: "POST",
          body: new FormData(form),
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Cart add failed");
        }

        await refreshHeaderCart();
        const cartPanel = document.getElementById("pulse-cart-panel");
        const cartToggle = document.querySelector("[data-pulse-cart-toggle]");
        openOverlay(cartPanel, cartToggle);
      } catch (error) {
        console.error("[Pulse v2] Quick add failed.", error);
        window.location.href = window.routes.cart_url || "/cart";
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = submit.dataset.pulseOriginalText || originalText;
        }
      }
    });
  }

  function focusReplacementHeader(header) {
    const candidates = Array.from(
      header.querySelectorAll(
        "[data-pulse-barcode-trigger], .pulse-header__search-input, [data-pulse-menu-toggle], [data-pulse-cart-toggle], [data-pulse-account-toggle], a[href]",
      ),
    );
    const focusTarget = candidates.find(
      (element) => element.isConnected && !element.hidden && element.getClientRects().length > 0,
    );
    (focusTarget || document.getElementById("MainContent"))?.focus({ preventScroll: true });
  }

  function unmountHeader() {
    const destroy = destroyHeaderController;
    const focusedElement = document.activeElement;
    const portaledModal = document.querySelector("[data-pulse-barcode-modal]");
    const headerOwnedFocus = Boolean(
      (activeHeaderElement && focusedElement && activeHeaderElement.contains(focusedElement)) ||
      (portaledModal && focusedElement && portaledModal.contains(focusedElement)) ||
      (portaledModal && !portaledModal.hidden),
    );
    if (headerOwnedFocus) {
      restoreHeaderFocusOnMount = true;
    }

    destroyHeaderController = null;
    activeHeaderElement = null;

    if (destroy) {
      let cleanup;
      try {
        cleanup = destroy();
      } catch (error) {
        cleanup = Promise.reject(error);
      }
      headerTeardownPromise = Promise.all([headerTeardownPromise, Promise.resolve(cleanup)]).then(() => undefined);
    }

    if (headerOwnedFocus) {
      const mainContent = document.getElementById("MainContent");
      mainContent?.focus({ preventScroll: true });
      window.requestAnimationFrame(() => {
        if (!restoreHeaderFocusOnMount) return;
        restoreHeaderFocusOnMount = false;
        mainContent?.focus({ preventScroll: true });
      });
    }

    return headerTeardownPromise;
  }

  function mountHeader(headerRoot) {
    const header = headerRoot || document.querySelector("[data-pulse-header]");
    if (!header || header === activeHeaderElement) return;

    const previousHeaderCleanup = unmountHeader();
    const shouldRestoreFocus = restoreHeaderFocusOnMount;
    if (shouldRestoreFocus) {
      restoreHeaderFocusOnMount = false;
    }

    activeHeaderElement = header;
    destroyHeaderController = initHeader(header, previousHeaderCleanup);

    if (shouldRestoreFocus) {
      focusReplacementHeader(header);
      window.requestAnimationFrame(() => focusReplacementHeader(header));
    }
  }

  function initializePulseV2() {
    createIcons();
    mountHeader();
    initQuickAdd();
    document.querySelectorAll("[data-pulse-hero]").forEach((root) => initHero(root));
    initFeedModules();
    syncBodyState();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const nextHeader = event.target.matches?.("[data-pulse-header]")
      ? event.target
      : event.target.querySelector?.("[data-pulse-header]");
    if (nextHeader) {
      mountHeader(nextHeader);
      createIcons();
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (activeHeaderElement && (event.target === activeHeaderElement || event.target.contains?.(activeHeaderElement))) {
      unmountHeader();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializePulseV2, { once: true });
  } else {
    initializePulseV2();
  }
})();
